import React, { useEffect, useState } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import Paper from '@material-ui/core/Paper';
import { createStyles, Theme, makeStyles } from '@material-ui/core/styles';
import CircularProgress from '@material-ui/core/CircularProgress';
import Grid from '@material-ui/core/Grid';
import { showToast, BackTo } from '@deeptrue-frontend/ui';

import { useFormatMessage } from 'components/IntlProvider';
import { Dashboard } from 'components/Layout';
import api from 'api/api';
import StoreProvider from 'components/StoreProvider/StoreProvider';
import { WorkbenchesReducerType } from 'store/modules/workbenches/workbenches.types';
import Forms from 'components/Forms';
import RouterProvider from 'components/RouterProvider/RouterProvider';

const { ROUTES } = RouterProvider;
const { connect, selectors, actions } = StoreProvider;

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: '100%',
      maxWidth: '420px',
      marginTop: theme.spacing(3),
      padding: theme.spacing(2),
      border: '1px solid #CECECA',
      boxShadow: ' 0 4px 12px 0 rgba(0,0,0,0.1)',
      borderRadius: '10px',
    },
  })
);

interface WorkbenchProps extends RouteComponentProps<{ id: string }> {
  workbench: WorkbenchesReducerType['selected'];
  setWorkbench: (payload: WorkbenchesReducerType['selected']) => void;
  clearWorkbench: () => void;
}

const Workbench = ({
  workbench,
  setWorkbench,
  clearWorkbench,
  location,
  history,
  match: { params },
}: WorkbenchProps) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const classes = useStyles();
  const t = useFormatMessage();
  const isEditMode = params.id !== 'create';

  const submitWorkbench = (values: WorkbenchesReducerType['selected']) => {
    const formData = new FormData();
    formData.append('id', values.id.toString());
    formData.append('icon', values.icon);
    formData.append('icon_url', values.icon_url);
    formData.append('name', values.name);
    formData.append('slug', values.slug);
    formData.append('active', values.active.toString());
    if (isEditMode) {
      return api.resources
        .updateWorkbench(formData)
        .then(() => {
          history.push(ROUTES.ADMIN.WORKBENCHES.LIST);
        })
        .catch((err) => {
          Forms.helpers.throwError(err.response.data);
        });
    }

    return api.resources
      .createWorkbench(formData)
      .then(() => {
        history.push(ROUTES.ADMIN.WORKBENCHES.LIST);
      })
      .catch((err) => {
        Forms.helpers.throwError(err.response.data);
      });
  };

  const getWorkbench = () => {
    setIsLoaded(false);
    api.resources
      .getWorkbench({ id: params.id })
      .then(({ data }) => {
        setWorkbench(data);
        setIsLoaded(true);
      })
      .catch(() => {
        showToast({
          type: 'error',
          title: t(
            'errors.toast.workbench',
            'The workbench given cannot be found'
          ),
        });
        history.push(ROUTES.ADMIN.WORKBENCHES.LIST);
      });
  };

  useEffect(() => {
    if (isEditMode) {
      getWorkbench();
    } else {
      setIsLoaded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  useEffect(() => {
    return () => {
      clearWorkbench();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Dashboard
      title={isEditMode ? workbench.name : 'New workbench'}
      location={location}
    >
      <BackTo onClick={() => history.push('/admin/workbenches')}>
        {t('backTo.admin.workbenches', 'Back to Workbenches')}
      </BackTo>
      <Paper className={classes.root}>
        {isLoaded ? (
          <Forms.WorkbenchInfo
            initialValues={{
              ...workbench,
              isEditMode,
            }}
            onSubmit={submitWorkbench}
          />
        ) : (
          <Grid container justify="center">
            <CircularProgress />
          </Grid>
        )}
      </Paper>
    </Dashboard>
  );
};

export default connect(
  (state) => ({
    workbench: selectors.workbenches.selected(state),
  }),
  {
    setWorkbench: actions.workbenches.setWorkbench,
    clearWorkbench: actions.workbenches.clearWorkbench,
  }
)(Workbench);
